import React, { useEffect, useReducer } from 'react'
import Head from 'next/head'
import {
  getParentDepartment,
  useLuxLabel,
  useLuxMeasure,
} from '@moonpig/web-core-utils'
import { Page, PageRedirectResponse } from '@moonpig/web-core-app'
import {
  FavouritesProvider,
  getGiftUpsellProductId,
  GetUpsellProductQuery,
  GetUpsellProductGQL,
  GetUpsellProductQueryVariables,
  isCardProduct,
  ProductInitialVariantSelectionProvider,
  ProductModalProvider,
  UpsellProduct,
  ProductStoreProvider,
  ProductTrackingProvider,
  BasketProvider,
  PageTypeEnum,
  getCardVariantIdsEnabled,
  GetMembershipDetailsForUserQuery,
  GetMembershipDetailsForUserQueryVariables,
  GetMembershipDetailsGQL,
  MembershipContext,
  MembershipProvider,
  isDigitalGiftCard,
  hasMultipleVariants,
} from '@moonpig/web-shared-products'
import { Routes } from '@moonpig/web-core-routing'
import { Region } from '@moonpig/web-core-types'
import { logger } from '@moonpig/web-core-monitoring'
import { DepartmentsEnum } from '@moonpig/web-explore-types-graphql'
import { ApplyDiscountVoucher } from '@moonpig/web-shared-promotions'
import { useFlags } from '@moonpig/web-explore-flags'
import { isLoggedIn } from '@moonpig/web-core-auth'
import {
  ProductDetails,
  ProductWithRecommendations,
} from '../../components/ProductDetails'
import {
  GetDepartmentInfoQuery,
  GetDepartmentInfoQueryVariables,
} from '../../queries/__generated__/GetDepartmentInfo'
import { GetDepartmentInfoGQL } from '../../queries/getDepartmentInfo'
import { DepartmentInfo } from '../../types'
import { useLuxAddExperimentData } from '../../utils'
import {
  GetProductDetailsQuery,
  GetProductDetailsQueryVariables,
} from '../../queries/__generated__/GetProductDetails'
import { GetProductDetailsGQL } from '../../queries/getProductDetails'
import { useFindLocaleText } from '../../text-localisation'
import { getSearchExperimentString } from '../../utils/getExperimentValues'
import { ProductDetailsModular } from '../../components/ProductDetailsModular'
import {
  ProductDetailsContext,
  ProductProvider,
  productStateReducer,
} from '../../contexts/product'
import { ProductDetailsStateActionTypes } from '../../contexts/product/types'
import { getModularLayout } from '../../utils/getModularLayout'
import { useGATrackingScreenViewWithProductEvent } from './useGATrackingScreenViewWithProductEvent'

type GetDepartmentInfo_contentDepartments =
  GetDepartmentInfoQuery['contentDepartments'][0]
type ContentDepartment = GetDepartmentInfo_contentDepartments

const DEFAULT_DEPARTMENT = 'ALL_CARDS' as DepartmentsEnum

const departmentLookup: { [key: string]: string } = {
  gifts: 'ALL_GIFTS' as DepartmentsEnum,
  cadeaus: 'ALL_GIFTS' as DepartmentsEnum,
  'personalised-mugs': 'ALL_GIFTS' as DepartmentsEnum,
  'flowers-and-plants': 'ALL_FLOWERS_AND_PLANTS' as DepartmentsEnum,
  'bloemen-en-planten': 'ALL_FLOWERS_AND_PLANTS' as DepartmentsEnum,
  'personalised-cards': 'ALL_CARDS' as DepartmentsEnum,
  'personalized-cards': 'ALL_CARDS' as DepartmentsEnum,
  wenskaarten: 'ALL_CARDS' as DepartmentsEnum,
}

const findDepartment = (
  department: DepartmentsEnum,
  departments: ContentDepartment[],
): DepartmentInfo => {
  const parentDepartment = getParentDepartment(department)

  return (
    departments.reduce<DepartmentInfo | null>((acc, curr) => {
      return (curr.id as DepartmentsEnum) === department // 1. exact match
        ? curr
        : curr.subdepartments.find(
            subdept => (subdept.id as DepartmentsEnum) === department,
          ) || // 2. sub-department match
            acc || // 3. existing parent match
            ((curr.id as DepartmentsEnum) === parentDepartment && curr) || // 4. parent match
            null // 5. no match
    }, null) || {
      longDeliveryInfo: '',
      dispatchCutOff: {
        weekdays: { message: '', time: '' },
        weekendsAndBankHolidays: { message: '', time: '' },
      },
    }
  )
}

const isUkCard = (region: Region, department: DepartmentsEnum): boolean =>
  region === 'uk' && isCardProduct(department)

const isDigitalGift = (department: DepartmentsEnum): boolean =>
  department === DepartmentsEnum.DIGITAL_GIFTS

const useModularLayout = ({
  product,
  upsellProduct,
  departmentInfo,
  flags,
}: {
  product: ProductWithRecommendations
  departmentInfo: DepartmentInfo
  upsellProduct?: UpsellProduct
  flags: ReturnType<typeof useFlags>
}) => {
  const [productState, dispatch] = useReducer(productStateReducer, {
    product,
    upsellProduct,
    departmentInfo,
    selectedOptions: {
      variantKey: product.masterVariant.key,
      quantity: 1,
      addonSku: null,
    },
  })

  const { department } = product.category

  const useMasterVariantAsDefault = !(
    isDigitalGiftCard(department) && hasMultipleVariants(product.variants)
  )

  useEffect(() => {
    dispatch({
      type: ProductDetailsStateActionTypes.SET_PRODUCT,
      payload: {
        product,
        useMasterVariantAsDefault,
        upsellProduct,
        departmentInfo,
      },
    })
  }, [product, departmentInfo, upsellProduct, useMasterVariantAsDefault])

  const productContext: ProductDetailsContext = [productState, dispatch]

  const showGiftAndFlowerModularPdp =
    !isCardProduct(department) && !isDigitalGift(department)

  const showDigitalGiftModularPdp =
    flags['modular-digital-gift-pdp'] && isDigitalGift(department)

  const showModularPdp =
    showGiftAndFlowerModularPdp || showDigitalGiftModularPdp

  if (showModularPdp) {
    return (
      <BasketProvider>
        <ProductProvider context={productContext}>
          <ProductDetailsModular layout={getModularLayout(product)} />
        </ProductProvider>
      </BasketProvider>
    )
  }
}

const isUrlSlugWrong = (
  product: ProductWithRecommendations | null,
  {
    slug,
    region,
    departmentSlug,
    asPath,
  }: {
    slug: string
    region: string
    departmentSlug: string
    asPath: string | undefined
  },
): PageRedirectResponse | null => {
  const searchParams = asPath?.split('?')[1]

  if (!product || !product.slug) return null

  if (product.slug !== slug) {
    return {
      pageRedirect: {
        statusCode: 301,
        location: `/${region}/${departmentSlug}/p/${product.slug}/${
          product.id
        }/${searchParams ? `?${searchParams}` : ''}`,
      },
    }
  }
  return null
}

export type ProductDetailsPageProps = {
  departmentSlug: string
  departmentInfo: DepartmentInfo
  membershipInformation: MembershipContext | null
  product: ProductWithRecommendations
  preselectedSize: string | undefined
  region: Region
  hostUrl: string
  upsellProduct?: UpsellProduct
  uqd?: string
}

export const ProductDetailsPage: Page<
  ProductDetailsPageProps,
  Routes['product-details']
> = ({
  departmentSlug,
  departmentInfo,
  membershipInformation,
  product,
  preselectedSize,
  region,
  hostUrl,
  upsellProduct,
  uqd,
}) => {
  const flags = useFlags()
  const localiseText = useFindLocaleText()

  const clientSideRoutingEnabled = false
  const luxAbTestCustomData = [
    {
      name: 'clientSideRouting',
      value: clientSideRoutingEnabled.toString(),
    },
  ]
  useLuxLabel('Product Details Page')
  useLuxMeasure(clientSideRoutingEnabled)
  useLuxAddExperimentData(luxAbTestCustomData)

  const {
    title,
    masterVariant: { images },
    description,
    slug,
    id,
    category: { department },
    variants,
  } = product

  const canonicalUrl = `${hostUrl}/${region}/${departmentSlug}/p/${slug}/${id}/`

  const modularLayout = useModularLayout({
    product,
    departmentInfo,
    upsellProduct,
    flags,
  })

  useGATrackingScreenViewWithProductEvent(product)

  return (
    <>
      <Head>
        {/* should be metaTitle or meta.title - a new addition to the graph */}
        <title>{localiseText('find.page_productdetails', { title })}</title>
        <meta name="title" content={title} />

        {/* should be metaDescription or meta.decription - a new addition to the graph */}
        {isUkCard(region, department) ? (
          <meta
            name="description"
            content={`${title}. ${localiseText('find.pdp_card_cut_off')}`}
          />
        ) : (
          <meta name="description" content={description.substring(0, 160)} />
        )}
        {department === DepartmentsEnum.DIGITAL_GIFT_CARDS && (
          <meta name="robots" content="noindex, nofollow" />
        )}
        <meta property="og:url" content={canonicalUrl} />
        {images.length && (
          <meta property="og:image" content={images[0].url} key="image" />
        )}
        <link rel="canonical" href={canonicalUrl} />
        <style>{`
          @media (max-width: 640px) {
            body {
              padding-bottom: 81px;
            }
          }
        `}</style>
        {isCardProduct(department) && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org/',
                '@type': 'Product',
                sku: id,
                name: title,
                description,
                image: images.map(image => image.url),
                offers: variants.map(({ price, sku, inStock }) => ({
                  '@type': 'Offer',
                  sku,
                  priceCurrency: price.currencyCode,
                  price: price.centAmount / 10 ** price.fractionDigits,
                  availability: inStock
                    ? 'https://schema.org/InStock'
                    : 'https://schema.org/OutOfStock',
                })),
              }),
            }}
          />
        )}
      </Head>
      <ApplyDiscountVoucher uqd={uqd} />
      <MembershipProvider membershipInformation={membershipInformation}>
        <BasketProvider>
          <ProductInitialVariantSelectionProvider variantKey={preselectedSize}>
            <FavouritesProvider
              location={title}
              enableToastNotification={flags['enable-fav-toast-notification']}
            >
              <ProductModalProvider
                context={{
                  location: title,
                  pageType: PageTypeEnum.PDP,
                  flags,
                }}
              >
                {modularLayout || (
                  <ProductStoreProvider
                    initialProduct={product}
                    initialVariantKey={preselectedSize}
                  >
                    <ProductTrackingProvider
                      pageLocation="product details"
                      pageComponent="product details"
                    >
                      <ProductDetails
                        product={product}
                        departmentInfo={departmentInfo}
                        departmentSlug={departmentSlug}
                        url={canonicalUrl}
                      />
                    </ProductTrackingProvider>
                  </ProductStoreProvider>
                )}
              </ProductModalProvider>
            </FavouritesProvider>
          </ProductInitialVariantSelectionProvider>
        </BasketProvider>
      </MembershipProvider>
    </>
  )
}

ProductDetailsPage.layout = 'default'

ProductDetailsPage.getInitialProps = async ({
  region,
  query,
  graphQL,
  getCookie,
  environment,
  asPath,
  getExperiment,
}) => {
  const {
    departmentSlug,
    id,
    size: preselectedSize,
    region: queryRegion,
    slug,
    uqd,
  } = query

  const baseDepartment = departmentLookup[departmentSlug] || DEFAULT_DEPARTMENT
  const experimentValues = getSearchExperimentString()
  const isAuthenticated = isLoggedIn(getCookie)

  const peopleAlsoViewedExperiment = getExperiment(
    'people-also-viewed-alternative-algorithm',
    {
      fallback: 'control',
    },
  )

  const algorithmId =
    peopleAlsoViewedExperiment === 'control'
      ? 'CustomersAlsoViewed'
      : 'CustomersAlsoViewed_variant'

  if (!id || !departmentSlug) {
    logger.fixToday(
      `id (${id}) or department slug (${departmentSlug}) was not supplied `,
    )
    return {
      pageError: {
        statusCode: 404,
      },
    }
  }

  const variants =
    baseDepartment === ('ALL_CARDS' as DepartmentsEnum)
      ? getCardVariantIdsEnabled()
      : []

  try {
    const departmentsQuery = graphQL.query<
      GetDepartmentInfoQuery,
      GetDepartmentInfoQueryVariables
    >({
      query: GetDepartmentInfoGQL,
      variables: {
        filter: [baseDepartment],
        preview: undefined,
      },
    })

    const productsQuery = graphQL.query<
      GetProductDetailsQuery,
      GetProductDetailsQueryVariables
    >({
      query: GetProductDetailsGQL,
      variables: {
        productId: id,
        variants,
        experimentValues,
        algorithmId,
      },
    })

    const membershipsDetailsQuery = graphQL.query<
      GetMembershipDetailsForUserQuery,
      GetMembershipDetailsForUserQueryVariables
    >({
      query: GetMembershipDetailsGQL,
      variables: {
        isLoggedIn: isAuthenticated,
      },
    })

    const giftUpsellProductId = getGiftUpsellProductId(id)

    const upsellProductQuery = giftUpsellProductId
      ? graphQL.query<GetUpsellProductQuery, GetUpsellProductQueryVariables>({
          query: GetUpsellProductGQL,
          variables: {
            productId: giftUpsellProductId,
          },
        })
      : Promise.resolve({ data: undefined, errors: [] })

    const [
      { data: departmentsData, errors: departmentsErrors },
      { data: productData, errors: productErrors },
      { data: upsellProductData, errors: upsellProductErrors },
      { data: membershipDetailsData, errors: membershipDetailsFetchErrors },
    ] = await Promise.all([
      departmentsQuery,
      productsQuery,
      upsellProductQuery.catch(error => ({ data: undefined, errors: [error] })),
      membershipsDetailsQuery.catch(error => ({ data: null, errors: [error] })),
    ])

    if (departmentsErrors?.length) {
      logger.warning(
        `Unable to fetch department info for ${departmentSlug}`,
        undefined,
        new Error(JSON.stringify(departmentsErrors)),
      )
    }

    if (upsellProductErrors?.length) {
      logger.warning(
        `Unable to fetch upsell product, (id: ${giftUpsellProductId})`,
        undefined,
        new Error(JSON.stringify(upsellProductErrors)),
      )
    }

    if (membershipDetailsFetchErrors?.length) {
      logger.warning(
        `Unable to fetch membership information`,
        undefined,
        new Error(JSON.stringify(membershipDetailsFetchErrors)),
      )
    }

    const upsellProduct = upsellProductData?.productLookup?.products[0]

    const departments = departmentsData?.contentDepartments || []

    if (productErrors?.length) {
      throw Error(JSON.stringify(productErrors))
    }

    const product =
      productData?.productLookup?.products.length === 1
        ? ({
            ...productData?.productLookup?.products[0],
            personalisation: {},
          } as ProductWithRecommendations)
        : null

    const { isSponsored, internallyPromoted } = JSON.parse(
      decodeURIComponent(getCookie('UIH') || '{}'),
    )

    const productWithSponsored = product
      ? ({
          ...product,
          isSponsored: isSponsored ?? false,
          internallyPromoted: internallyPromoted ?? false,
        } as ProductWithRecommendations)
      : null

    const redirect = isUrlSlugWrong(product, {
      departmentSlug,
      region: queryRegion,
      slug,
      asPath,
    })
    if (redirect) {
      return redirect
    }

    if (!productWithSponsored) {
      return {
        pageError: {
          statusCode: 404,
        },
      }
    }

    const departmentInfo = findDepartment(
      productWithSponsored.category.department,
      departments,
    )

    const customer = membershipDetailsData?.me?.customer

    const membershipInformation = customer?.membership
      ? { customerName: customer.firstName }
      : null

    return {
      departmentSlug,
      departmentInfo,
      membershipInformation,
      product: productWithSponsored,
      preselectedSize,
      region,
      hostUrl: environment.hostUrl,
      pageOptions: {
        tracking: {
          type: 'product-details',
          disableDefaultPageTracking: true,
        },
      },
      upsellProduct,
      uqd,
    }
  } catch (error) {
    logger.fixImmediately('Failed to load PDP', error)
    return {
      pageError: {
        statusCode: 404, // we don't have a 500 page with the meganav so we're using 404 here
      },
    }
  }
}
